/** @jsxImportSource @emotion/react */
import { Add, Check, Close, Error, ReceiptLong, Remove, Settings } from '@mui/icons-material';
import { Chip, Grid, IconButton, Typography, useTheme } from '@mui/material';
import { DataGrid, GridCellParams, GridColDef, GridSortItem, GridSortModel, GridValueFormatterParams } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';

import { stripedDateGrid } from '../../../common/CustumEmotionStyles';
import { remToPixels, StatusIcon } from '../../../common/CustomFunctions';
import { IFileSortItem, ISourceDocumentModel } from '../../../service/model/ISourceDocumentModel';
import { CellPopper } from '../../../common/components/CellPopper';
import { useTranslation } from 'react-i18next';

interface IProps {
    rows: ISourceDocumentModel[] | null,
    errors: string[],
    limitPerPage: number,
    totalItems: number,
    loader: boolean,
    changePage: (page: number) => void
    toggleDocView: (index?: number) => void,
    sortItem: (sortItem: IFileSortItem | null) => void,
    activeIndex: number | null
}

function onlyUnique(value: any, index: number, self:any) {
    return self.indexOf(value) === index;
}
function sortDate(a: string, b: string) {
    return new Date(b).getTime() - new Date(a).getTime()
}

const DatagridVoucher = (props: IProps): JSX.Element => {

    const theme = useTheme();
    const [gridRows, setGridRows] = useState<any>([]);

    const { t } = useTranslation();

    const ToggleGridViewButton = (prop: { param: GridCellParams }) => {
        if (props.activeIndex !== null && props.activeIndex === props.rows?.findIndex(e => e.id === parseInt(prop.param.id.toString()) as number)) {
            return <IconButton onClick={() => props.toggleDocView()}>
                <Remove />
            </IconButton>
        } else {
            return <IconButton onClick={() =>
                props.toggleDocView(!isNaN(parseInt(prop.param.id.toString())) ?
                    props.rows?.findIndex(e => e.id === parseInt(prop.param.id.toString()) as number) : undefined)}>
                <Add />
            </IconButton>
        }
    }

    useEffect(() => {

        let rows = props.rows ? props.rows.map(elem => {
            return {
                id: elem.id,
                status: elem.status,
                invoiceNo: elem.lines?.map(e => e.invoiceNo).filter(e => e && e !== "").filter(onlyUnique).join(",\n"),
                voucherDate: elem.lines?.
                    map(e => e.voucherDate ? new Date(e.voucherDate).toISOString() : "").
                    filter(onlyUnique).
                    filter(e => e !== "").
                    sort(sortDate).map(e => new Date(e).toLocaleDateString("nb-NO")).
                    join(",\n"),
                dueDate: elem.lines?.
                    map(e => e.dueDate ? new Date(e.dueDate).toISOString() : "").
                    filter(onlyUnique).
                    filter(e => e !== "").
                    sort(sortDate).map(e => new Date(e).toLocaleDateString("nb-NO")).
                    join(",\n"),
                amount: elem.lines?.filter(e => e.debitAccount !== null && e.debitAccount !== undefined).map(e => e.amount ? e.amount : 0).reduce((acc, value) => { return acc + value }, 0),
                voucherId: elem.voucherId,
                debitAccount: elem.lines?.map(e => e.debitAccount ? e.debitAccount.name + " (" + e.debitAccount.accountId + ")" : "").filter(e => e && e !== "").filter(onlyUnique).join(",\n"),
                creditAccount: elem.lines?.map(e => e.creditAccount ? e.creditAccount.name + " (" + e.creditAccount.accountId + ")" : "").filter(e => e && e !== "").filter(onlyUnique).join(",\n"),
                department: elem.lines?.map(e => e.department ? e.department.name + " (" + e.department.departmentId + ")" : "").filter(e => e && e !== "").filter(onlyUnique).join(",\n"),
                project: elem.lines?.map(e => e.project ? e.project.name + " (" + e.project.projectId + ")" : "").filter(e => e && e !== "").filter(onlyUnique).join(",\n"),
                valueDate: elem.lines?.
                    map(e => e.valueDate ? new Date(e.valueDate).toISOString() : "").
                    filter(onlyUnique).
                    filter(e => e !== "").
                    sort(sortDate).map(e => new Date(e).toLocaleDateString("nb-NO")).
                    join(",\n"),
                type: elem.type,
                createdDate: elem.createdDate ? new Date(elem.createdDate).toLocaleDateString("nb-NO") : undefined,
                comments: elem.comments ? elem.comments : undefined,
            }
        }) : [];

        setGridRows(rows);

    }, [props.rows, props.limitPerPage, props.totalItems])

    function typeLanguage(type: string) {
        switch (type) {
            case "IncomingInvoice": //Avhuking
                return t("types.incomingInvoice");
            case "OutgoingInvoice": //Avhuking
                return t("types.outgoingInvoice");
            case "Banking": //Tannhjul
                return t("types.banking");
            case "Cash": //Tannhjul
                return t("types.cash");
            case "Statement": //Tannhjul
                return t("types.statement");
            case "Addition": //Tannhjul
                return t("types.addition");
            case "JournalEntries": //Tannhjul
                return t("types.journalEntries");
            case "Miscellaneous": //Kryss
                return t("types.miscellaneous");
        }
    }

    const columns: GridColDef[] = [
        {
            field: 'id', headerName: 'id', width: 60, hide: true,
            //renderCell: () => (<Check fontSize="large" color="success" />)
        },
        {
            field: 'openDoc', headerName: '', width: 60, sortable: false,
            renderCell: (param: GridCellParams) => (
                <ToggleGridViewButton param={param} />
            )
        },
        {
            field: 'status', headerName: t('search.status'), width: 60, align: "center",
            renderCell: (params: GridCellParams) => <StatusIcon status={params.value}/>
        },
        {
            field: 'invoiceNo', headerName: t('search.invoiceId'), width: 120,
            renderCell: CellPopper,
        },
        { field: 'voucherDate', headerName: t('search.voucherDate'), width: 105, renderCell: CellPopper, },
        {
            field: 'dueDate', headerName: t('search.dueDate'), width: 105,
            renderCell: CellPopper,
        },
        {
            field: 'amount', headerName: t('search.amount'), width: 130, align:"right",
            valueFormatter: (params: GridValueFormatterParams<number>) => {
                if (params.value == null) {
                    return "";
                }
                return new Intl.NumberFormat('nb-NO', { minimumFractionDigits:2, maximumFractionDigits: 2 }).format(params.value);
            }
        },
        {
            field: 'voucherId', headerName: t('search.voucherId'), width: 130,
            renderCell: (params: GridCellParams) => {
                if (params.value) {
                    return <Chip variant="outlined" size="small" icon={< ReceiptLong />} label={params.value} color="warning" />;
                } else {
                    return "";
                }
            }
        },
        {
            field: 'debitAccount', headerName: t('search.debet'), width: 210,
            renderCell: CellPopper,
        },
        {
            field: 'creditAccount', headerName: t('search.credit'), width: 210,
            renderCell: CellPopper,
        },
        { field: 'department', headerName: t('search.department'), width: 170, renderCell: CellPopper, },
        { field: 'project', headerName: t('search.project'), width: 170, renderCell: CellPopper, },
        { field: 'valueDate', headerName: t('search.valueDate'), width: 120, renderCell: CellPopper, },
        { field: 'type', headerName: t('search.type'), width: 130, valueGetter: (param: GridCellParams) => typeLanguage(param.value) },
        { field: 'createdDate', headerName: t('search.created'), width: 120, },
        { field: 'comments', headerName: t('search.comment'), width: 120, flex:1, renderCell: CellPopper},
    ];

    function CustomErrorOverlay() {
        return <Grid container direction="column" justifyContent="center" alignItems="center" height="100%">
            <Grid item><Error fontSize="large"/></Grid>
            <Grid item width="15rem" display="flex" justifyContent="center">
                <Typography variant="body2">
                    {t("basic.unexpectedError")}
                </Typography>
            </Grid>
        </Grid>
    }

    const [sortModel, setSortModel] = useState<GridSortModel | undefined>()
    const handleSortModelChange = (newModel: GridSortModel) => {
        setSortModel(newModel);
        //This datagrid only handles single column filter.
        if (newModel.length === 1 && (newModel[0].sort === "desc" || newModel[0].sort === "asc")) {
            let sItem: IFileSortItem = {field: newModel[0].field, sort: newModel[0].sort}
            props.sortItem(sItem); //Add filter
        } else{
            props.sortItem(null); //Remove filter. //Does not call API if empty string from before.
        }
    };

    return <DataGrid css={stripedDateGrid(theme, theme.palette.grey[200])}
        rows={gridRows}
        columns={columns}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        rowHeight={remToPixels(2)}
        pagination
        paginationMode="server"
        pageSize={props.limitPerPage}
        rowCount={props.totalItems}
        loading={props.loader}
        onPageChange={(newPage) => {
            props.changePage(newPage);
        }}
        headerHeight={remToPixels(4)}
        getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        error={props.errors && props.errors.length > 0 ? true : undefined}

        localeText={{
            toolbarColumns: t("muiDatagrid.columns"),
            toolbarColumnsLabel: t("muiDatagrid.columnsLabel"),
            columnsPanelTextFieldPlaceholder: t("muiDatagrid.columnTitle"),
            columnsPanelTextFieldLabel: t("muiDatagrid.columnFind"),
            columnsPanelShowAllButton: t("muiDatagrid.showAll"),
            columnsPanelHideAllButton: t("muiDatagrid.hideAll"),

            columnMenuFilter: t("muiDatagrid.filter"),
            columnMenuHideColumn: t("muiDatagrid.hide"),
            columnMenuLabel: t("muiDatagrid.label"),
            columnMenuShowColumns: t("muiDatagrid.showColumns"),
            columnMenuSortAsc: t("muiDatagrid.sortAsc"),
            columnMenuSortDesc: t("muiDatagrid.sortDesc"),
            columnMenuUnsort: t("muiDatagrid.unsort"),

            toolbarExport: t("muiDatagrid.export"),
            toolbarExportLabel: t("muiDatagrid.exportLabel"),
            toolbarExportCSV: t("muiDatagrid.exportCSV"),
            toolbarExportPrint: t("muiDatagrid.exportPrint"),
            toolbarExportExcel: t("muiDatagrid.exportExcel"),
        }}

        components={{
            ErrorOverlay: CustomErrorOverlay,
        }} />
}

export default DatagridVoucher;